import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import usePageMeta from '@/hooks/usePageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = useMemo(() => articles.find((a) => a.id === id), [id]);
  const category = useMemo(
    () => (article ? categories.find((c) => c.slug === article.category) : undefined),
    [article]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && (a.category === article.category || a.studio === article.studio))
      .slice(0, 4);
  }, [article]);

  const articleSchema = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.description,
        datePublished: article.releaseDate,
        author: { '@type': 'Organization', name: article.studio },
        publisher: { '@type': 'Organization', name: '番号吧' },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://miaoxiaobao.cn/article/${article.id}`,
        },
      }
    : null;

  const breadcrumbSchema = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://miaoxiaobao.cn/' },
          {
            '@type': 'ListItem',
            position: 2,
            name: category?.name || '分类',
            item: `https://miaoxiaobao.cn/category/${article.category}`,
          },
          { '@type': 'ListItem', position: 3, name: article.code },
        ],
      }
    : null;

  const jsonLd = [articleSchema, breadcrumbSchema].filter(Boolean) as Record<string, unknown>[];

  usePageMeta({
    title: article ? `${article.code} - ${article.title} - 番号吧` : '番号吧',
    description: article?.description || '',
    keywords: article
      ? `${article.code},${article.title},${article.studio},${article.actors.join(',')},番号查询`
      : '',
    canonicalUrl: article ? `https://miaoxiaobao.cn/article/${article.id}` : 'https://miaoxiaobao.cn/',
    ogTitle: article ? `${article.code} - ${article.title}` : '',
    ogDescription: article?.description || '',
    ogType: 'article',
    ogUrl: article ? `https://miaoxiaobao.cn/article/${article.id}` : '',
    ogImage: article?.coverImage || '',
    jsonLd,
  });

  if (!article) {
    return <NotFound />;
  }

  return (
    <main>
      <Breadcrumb
        items={[
          { label: category?.name || '分类', href: `/category/${article.category}` },
          { label: article.code },
        ]}
      />

      <article className="w-full px-4 md:px-6 pb-12 md:pb-16">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="lg:w-[300px] shrink-0">
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100 sticky top-24">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.code} - ${article.title}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="mb-2">
                <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                  {article.code}
                </span>
              </div>

              <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-accent-100 text-accent-700 text-sm font-semibold rounded-full">
                  <i className="ri-star-fill text-xs"></i>
                  {article.rating}
                </span>
                <span className="text-sm text-foreground-500 flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万次浏览` : `${article.views}次浏览`}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6 p-4 rounded-xl bg-background-100">
                <div>
                  <p className="text-xs text-foreground-500 mb-1">厂商</p>
                  <p className="text-sm font-medium text-foreground-900">{article.studio}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">厂牌</p>
                  <p className="text-sm font-medium text-foreground-900">{article.label}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">发布日期</p>
                  <p className="text-sm font-medium text-foreground-900">{article.releaseDate}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">时长</p>
                  <p className="text-sm font-medium text-foreground-900">{article.duration}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-foreground-500 mb-1">演员</p>
                  <div className="flex flex-wrap gap-1">
                    {article.actors.map((actor) => (
                      <span
                        key={actor}
                        className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                      >
                        {actor}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-foreground-500 mb-1">导演</p>
                  <p className="text-sm font-medium text-foreground-900">{article.director}</p>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-base font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {article.description}
                </p>
              </div>

              <div className="mb-6">
                <h2 className="text-base font-semibold text-foreground-900 mb-2">标签</h2>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-background-100 border border-background-200 text-foreground-600 text-xs rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <h2 className="text-lg font-bold font-heading text-foreground-900 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <p className="text-xs text-primary-600 font-medium">{rel.code}</p>
                      <p className="text-sm text-foreground-800 line-clamp-2 leading-snug mt-1">
                        {rel.title}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </main>
  );
}